import type { CustomerVoiceViewModel } from '@/models/customerVoice';
import type { EnStoryViewModel } from '@/models/enStory';
import { stripHtmlTags } from '@/utils/stringUtils';
import type { ArticleColumn, ArticleSection, DrawerLink } from '@/wabunka-src/common/features/Featured/models/featured';
import type { EnFaqListViewModel } from '@/wabunka-src/features/FAQ/models/enFaq';

import type { MicroCMSImage } from 'microcms-js-sdk';

interface SubImage {
  fieldId: string;
  image: MicroCMSImage;
}

export interface Address {
  fieldId: string;
  postcode: string;
  address: string;
  latitude: string;
  longitude: string;
}

interface PickupSettings {
  fieldId: string;
  isPickup: boolean;
  pickupPriority: number;
}

export interface DrawerTab {
  fieldId: 'drawerTabs';
  tabKey: string;
  tabName: string;
  body?: string;
}

// 写真だけのタブは本文テキストが無くても入稿として有効なので、メディアがあれば表示する
const DRAWER_TAB_MEDIA_PATTERN = /<(img|iframe|video)[\s/>]/i;

// リッチエディターは末尾改行で `<p><br></p>` を残すため、タグを剥がしたテキストで空を判定する
const hasDrawerTabBody = (body: string | undefined): boolean => {
  if (!body) return false;
  const text = stripHtmlTags(body).replace(/(&nbsp;|&#160;|\s)+/gi, '');
  return text !== '' || DRAWER_TAB_MEDIA_PATTERN.test(body);
};

/**
 * 本文が空のタブは表示しない（ADR-145 / D-005）
 */
export const extractVisibleDrawerTabs = (drawerTabs: DrawerTab[] | undefined): DrawerTab[] => (drawerTabs ?? []).filter((tab) => hasDrawerTabBody(tab.body));

/**
 * 表示可能タブが0件なら起動リンク自体を描画しない（ADR-145 / D-005）
 */
export const canShowDrawerLink = (drawerTabs: DrawerTab[] | undefined): boolean => extractVisibleDrawerTabs(drawerTabs).length > 0;

/**
 * Overview の下に起動リンクを出すか。記事セクション内のリンクと同じく表示可能タブが要る（D-005）。
 * ラベルが空だと押せる文字の無いボタンになるため出さない（D-022）
 */
export const canShowOverviewDrawerLink = (overviewDrawerLink: DrawerLink | null | undefined, drawerTabs: DrawerTab[] | undefined): boolean =>
  canShowDrawerLink(drawerTabs) && !!overviewDrawerLink?.label?.trim();

/**
 * 起動リンクが開くタブの位置を返す。tabKey が未指定・不一致・指す先の本文が空 のいずれでも先頭の
 * 表示可能タブを指す（ADR-145 / D-006）。表示可能タブが0件なら -1。
 * tabKey は自由記述で前後の空白が混ざり得るため、突き合わせは前後を落として行う（D-003）
 */
export const resolveDrawerTabIndex = (drawerTabs: DrawerTab[] | undefined, tabKey: string | undefined): number => {
  const visibleTabs = extractVisibleDrawerTabs(drawerTabs);
  if (visibleTabs.length === 0) return -1;

  const targetTabKey = tabKey?.trim();
  if (!targetTabKey) return 0;

  const index = visibleTabs.findIndex((tab) => tab.tabKey?.trim() === targetTabKey);
  return index === -1 ? 0 : index;
};

export interface Content {
  fieldId: 'articleSection' | 'richEditor' | 'html' | 'images';
  richEditor: string | undefined;
  html: string | undefined;
  image: MicroCMSImage | undefined;
  columns?: ArticleColumn[] | undefined;
  reverseOrder?: boolean | undefined;
  backgroundColor?: string[] | undefined;
}
/**
 * ArticleSectionを構築する
 * NOTE: 特集ページと体験詳細ページで同様の記事セクションを管理している。しかし体験レスポンス内のcontent型が
 *       特集ページと異なっているのでこの関数で構造を揃えている。
 * @param content MicroCMS 体験レスポンス内のcontent型
 * @returns ArticleSection
 */
export const buildArticleSection = (content: Content): ArticleSection => {
  return {
    fieldId: 'articleSection',
    columns: content.columns ?? [],
    reverseOrder: content.reverseOrder ?? false,
    backgroundColor: content.backgroundColor ?? ['白'],
  };
};

/**
 * ArticleSectionを構築する
 * NOTE: 特集ページと体験詳細ページで同様の記事セクションを管理している。しかし体験レスポンス内のcontent型が
 *       特集ページと異なっているのでこの関数で構造を揃えている。
 * @param contents MicroCMS 体験レスポンス内のcontent型の配列
 * @returns ArticleSection[]
 */
export const buildArticleSectionContents = (contents: Content[]): ArticleSection[] => {
  return contents.filter((content) => content.fieldId === 'articleSection').map((content) => buildArticleSection(content));
};

/**
 * MicroCMS 返り値の型定義
 * NOTE: MicroCMS の以下ページで作成したフィールドと一致させておく
 * https://dev-calchez.microcms.io/apis/en-experiences
 */
export interface EnExperienceResponse {
  id: string; // slugとして使用
  title: string;
  pcMainImage: MicroCMSImage;
  spMainImage: MicroCMSImage;
  subImages: SubImage[];
  overviewV2: string;
  overviewDrawerLink?: DrawerLink | null;
  address: Address;
  contents: Content[];
  drawerTitle?: string;
  drawerTabs?: DrawerTab[];
  faq: EnFaqListViewModel;
  pickupSettings: PickupSettings | null;
  providerImg: MicroCMSImage;
  providerName: string;
  providerDesc: string;
  customerVoices: CustomerVoiceViewModel[] | null;
  stories: EnStoryViewModel[] | null;
  metaTitle?: string;
  metaDescription?: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  revisedAt: string;
}

export type EnExperienceViewModel = EnExperienceResponse;

// 一覧が空でも FAQ だけで読める中身があるため、両方とも空のときだけ出さない
// 型は required だが microCMS は繰り返しフィールドが 0 件のとき null を返すため、null / undefined を空として扱う
export const hasThingsToKnowSection = (experience: Pick<EnExperienceViewModel, 'drawerTabs' | 'faq'>): boolean => canShowDrawerLink(experience.drawerTabs) || (experience.faq ?? []).length > 0;

export const buildEnExperienceViewModel = (enExperience: EnExperienceResponse): EnExperienceViewModel => {
  return enExperience;
};

export const createDefaultEnExperienceViewModel = (): EnExperienceViewModel => ({
  id: '',
  title: '',
  pcMainImage: {
    url: '',
  },
  spMainImage: {
    url: '',
  },
  subImages: [],
  overviewV2: '',
  address: {
    fieldId: '',
    postcode: '',
    address: '',
    latitude: '',
    longitude: '',
  },
  contents: [],
  drawerTitle: '',
  drawerTabs: [],
  faq: [],
  pickupSettings: {
    fieldId: '',
    isPickup: false,
    pickupPriority: 0,
  },
  providerImg: {
    url: '',
  },
  providerName: '',
  providerDesc: '',
  customerVoices: [],
  stories: [],
  createdAt: '',
  updatedAt: '',
  publishedAt: '',
  revisedAt: '',
});

// Aliases for backward compatibility
export type MicroCMSExperienceResponse = EnExperienceResponse;
export type MicroCMSExperienceViewModel = EnExperienceViewModel;
export type MicroCMSExperienceListViewModel = EnExperienceViewModel[];
export const createDefaultMicroCMSExperienceViewModel = createDefaultEnExperienceViewModel;

/**
 * MicroCMS 返り値の型定義
 * NOTE: ビューで利用する必要最低限のフィールドに絞った型定義
 * https://dev-calchez.microcms.io/apis/en-experiences
 */
export interface MinimalMicroCMSExperienceResponse {
  id: string; // slugとして使用
  title?: string;
}

export type MinimalMicroCMSExperienceViewModel = MinimalMicroCMSExperienceResponse;
export type MinimalMicroCMSExperienceListViewModel = MinimalMicroCMSExperienceViewModel[];
